import React from 'react';
import omit from 'lodash/omit';
import { useControllerMirror, useDynamicFormsCtx, useSearch } from '../hooks';
import { callUnmout, getFieldMethods, initializeStore, updateStore } from './utils';
export const Controller = ({ spec: _spec, name, value: valueFromParent, parentOnChange, parentOnUnmount, }) => {
    const { config, tools, mutatorsStore, __mirror } = useDynamicFormsCtx();
    const firstRenderRef = React.useRef(true);
    const [store, setStore] = React.useState(initializeStore({
        name,
        spec: _spec,
        mutatorsStore,
        config,
        valueFromParent,
        tools,
        parentOnChange,
        parentOnUnmount,
    }));
    const { methods, fieldMethods } = React.useMemo(() => {
        const fieldMethods = getFieldMethods();
        const onChange = (valOrSetter, childErrors, errorMutator) => {
            setStore((store) => fieldMethods.onChange(store, { valOrSetter, childErrors, errorMutator }));
        };
        const onDrop = () => {
            setStore((store) => fieldMethods.onDrop(store, undefined));
        };
        const onBlur = () => {
            setStore((store) => fieldMethods.onBlur(store, undefined));
        };
        const onFocus = () => {
            setStore((store) => fieldMethods.onFocus(store, undefined));
        };
        const parentOnUnmount = (childName) => {
            setStore((store) => fieldMethods.parentOnUnmount(store, childName));
        };
        const onItemAdd = (value) => {
            setStore((store) => fieldMethods.onItemAdd(store, value));
        };
        const onItemRemove = (idx) => {
            setStore((store) => fieldMethods.onItemRemove(store, idx));
        };
        return {
            methods: {
                onChange,
                onDrop,
                onBlur,
                onFocus,
                parentOnUnmount,
                onItemAdd,
                onItemRemove,
            },
            fieldMethods,
        };
    }, [setStore]);
    const renderProps = React.useMemo(() => ({
        input: {
            name: store.name,
            value: store.state.value,
            onChange: methods.onChange,
            onBlur: methods.onBlur,
            onFocus: methods.onFocus,
            onDrop: methods.onDrop,
            parentOnUnmount: methods.parentOnUnmount,
        },
        arrayInput: {
            name: store.name,
            value: store.state.value,
            onItemAdd: methods.onItemAdd,
            onItemRemove: methods.onItemRemove,
            onDrop: methods.onDrop,
        },
        meta: Object.assign(Object.assign({}, omit(store.state, 'value')), { submitFailed: store.tools.submitFailed }),
    }), [methods, store.name, store.state, store.tools.submitFailed]);
    const withSearch = useSearch(store.spec, store.state.value, store.name);
    useControllerMirror(store.name, {
        useField: renderProps,
        useSearch: withSearch,
    }, __mirror);
    React.useEffect(() => {
        var _a;
        (_a = store.afterStoreUpdateCB) === null || _a === void 0 ? void 0 : _a.call(store);
    }, [store.afterStoreUpdateCB]);
    React.useEffect(() => {
        if (!firstRenderRef.current) {
            updateStore({
                store,
                setStore,
                spec: _spec,
                name,
                parentOnChange,
                parentOnUnmount,
                mutatorsStore,
                config,
                tools,
                methodOnChange: fieldMethods.onChange,
                valueFromParent,
            });
        }
    }, [
        _spec,
        name,
        parentOnChange,
        parentOnUnmount,
        mutatorsStore,
        config,
        tools.onChange,
        tools.onUnmount,
    ]);
    React.useEffect(() => {
        firstRenderRef.current = false;
        return () => {
            callUnmout(store);
        };
    }, []);
    return withSearch(store.render(renderProps));
};
